/**
 * ΣΥΜΦΩΝΙΑ ΚΑΤΑΛΟΓΟΥ — δείχνει πού ο κατάλογος ενός καταστήματος δεν συμφωνεί με
 * τον κεντρικό, και επιτρέπει τη διόρθωση με ένα κλικ.
 *
 * Γράφτηκε για χειρίστρια που δεν ξέρει (ούτε χρειάζεται να ξέρει) τι είναι
 * allow-list ή override. Γι' αυτό:
 *   • κάθε είδος διαφοράς έχει τίτλο στα ελληνικά και μια πρόταση που λέει ΤΙ
 *     θα γίνει αν πατήσει διόρθωση — όχι όνομα πεδίου, ρήμα,
 *   • οι διαφορές που εξηγούνται (τιμοκατάλογος, ρητή παρέκκλιση) δεν
 *     εμφανίζονται καθόλου· τις φιλτράρει ο διακομιστής,
 *   • τα «τοπικά» προϊόντα μόνο ΑΠΟΣΥΡΟΝΤΑΙ. Το ανέβασμα στον κεντρικό μένει
 *     χειροκίνητο: ο συγχρονισμός θα έφτιαχνε δεύτερο αντίγραφο σε κάθε
 *     κατάστημα που ήδη έχει το τοπικό (ο έλεγχος διπλότυπου του
 *     _syncProductAdd κοιτά ταυτότητα, όχι όνομα), και το ποιο κατάστημα
 *     δίνει την «σωστή» εκδοχή είναι απόφαση ανθρώπου.
 *
 * Νέα εμφάνιση (14/10/2026): μέσα στη σελίδα του κεντρικού καταλόγου (ListPage embedded). Οι τέσσερις
 * χρωματιστές κάρτες έγιναν φίλτρο «είδος» με τα πλήθη, τα καταστήματα «Πίνακας» (κλικ σε γραμμή = φίλτρο,
 * όπως πριν), τα ευρήματα λίστα χωρίς χρωματιστά κουτιά, οι δύο επιβεβαιώσεις συρτάρι. Τα ευρήματα μένουν
 * ΟΛΑ στη σελίδα (όχι ανά κατάστημα σε συρτάρι): το «όλα» μιας ομάδας επιλέγει σε ΟΛΑ τα καταστήματα
 * μαζί — αυτή είναι η μαζική διόρθωση των 27 καταστημάτων. Κλήσεις, σώματα και κανόνες επιλογής ίδια.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, ChevronDown, ChevronRight, Loader2, RefreshCw, Wrench } from 'lucide-react';
import { organizationAPI } from '../services/api';
import { LoadFailed } from './settings/instantSettings';
import {
  Column, DataTable, DetailPanel, EmptyState, GHOST_BTN, ListPage, ListToolbar, PRIMARY_BTN, StatusChip,
} from './shared/listPage';
import { Banner, FlashMessage, FlashToast, LINK_BTN } from './orgPages/ui';

type Kind = 'missing' | 'extra' | 'drift' | 'local';

interface FieldDiff {
  field: string;
  label: string;
  severity: 'high' | 'medium' | 'low';
  central: string;
  store: string;
  /** F1-W5-8: ρητή παρέκκλιση μεγέθους του καταστήματος — επιτρεπτή απόκλιση, δεν μετρά ως διαφορά. */
  allowed?: boolean;
  /** false = η διόρθωση δεν την αλλάζει (επιτρεπτή παρέκκλιση ή τοπικό μέγεθος ανεξάρτητου). */
  fixable?: boolean;
  /** F1-W5-8: τοπικό μέγεθος σε ανεξάρτητο οργανισμό — το κατάστημα το αποσύρει μόνο του. */
  local?: boolean;
  /** Τοπικό μέγεθος με το ίδιο όνομα με το κεντρικό — η διόρθωση το ΣΥΝΔΕΕΙ, δεν φτιάχνει δεύτερο. */
  unlinked?: boolean;
}

/** Γραμμή διαφοράς που η διόρθωση μπορεί να κλείσει (όχι επιτρεπτή παρέκκλιση / τοπικό μέγεθος). */
const isFixable = (d: FieldDiff) => d.fixable !== false && d.allowed !== true;

/** Γραμμή μεγέθους: `size:<org_size_id>` ή `size:local:<id>` (το push μεγεθών είναι ένα ανά προϊόν). */
const isSizeField = (field: string) => field.startsWith('size:');

interface Finding {
  kind: Kind;
  store_id: number;
  store_name: string;
  org_product_id: number | null;
  product_id: number | null;
  name: string;
  category_name: string | null;
  severity: 'high' | 'medium' | 'low';
  fields: FieldDiff[];
}

interface StoreTally {
  store_id: number;
  store_name: string;
  price_list_id: number | null;
  expected: number;
  missing: number;
  extra: number;
  drift: number;
  local: number;
}

/** Αλλαγές που έγιναν στον κεντρικό κατάλογο και περιμένουν έγκριση από το
 *  κάθε κατάστημα. Στην πράξη δεν μπαίνει κανείς να τις εγκρίνει, οπότε
 *  λιμνάζουν — γι' αυτό φαίνονται εδώ μαζί με κουμπί έγκρισης. */
interface PendingSync {
  total: number;
  stores: number;
  oldest: string | null;
  by_store: Array<{ store_id: number; store_name: string; n: number; oldest: string }>;
}

/** Τιμοκατάλογος που ορίζει μόνο βασική τιμή, ενώ ο κεντρικός έχει δική του
 *  τιμή καναλιού: στο κανάλι χρεώνεται ο κεντρικός, όχι η λίστα. */
interface PriceListGap {
  price_list_id: number;
  name: string;
  stores: string[];
  items: Array<{ product_name: string; size_name: string | null; channel: string; list_price: string; charged: string }>;
}

interface Report {
  organization_id: number;
  org_products: number;
  stores: StoreTally[];
  totals: Record<Kind, number>;
  findings: Finding[];
  price_list_gaps?: PriceListGap[];
  pending_sync?: PendingSync;
  cached: boolean;
}

/** Σειρά των ειδών (στο φίλτρο, στον πίνακα και στις ομάδες) και ο τόνος της ετικέτας τους. Τα κείμενα
 *  (τίτλος, εξήγηση, τι κάνει η διόρθωση) στο orgPages:consistency.kinds.<είδος>. */
const KIND_ORDER: Kind[] = ['missing', 'extra', 'drift', 'local'];
const KIND_TONE: Record<Kind, 'ok' | 'warn' | 'brand' | 'muted'> = {
  missing: 'ok', extra: 'warn', drift: 'brand', local: 'muted',
};

// Κλειδί επιλογής. Στις αποκλίσεις η επιλογή γίνεται ΑΝΑ ΣΤΟΙΧΕΙΟ, ώστε να
// μπορεί κανείς να διορθώσει την τιμή χωρίς να πειράξει π.χ. τον τύπο.
const selKey = (f: Finding, field?: string) =>
  `${f.kind}:${f.store_id}:${f.org_product_id ?? f.product_id}:${field || ''}`;

/** Τα κλειδιά επιλογής όλων των διορθώσιμων γραμμών μεγέθους ενός ευρήματος: ο συγχρονισμός
 *  γράφει όλα τα μεγέθη του προϊόντος μαζί, οπότε η οθόνη τα επιλέγει μαζί — να μη φαίνεται ότι
 *  διορθώνεται μόνο το «Μονός» όταν στην πράξη έρχονται στον κεντρικό και τα υπόλοιπα. */
const sizeKeysOf = (f: Finding): string[] =>
  Array.from(new Set(f.fields.filter(d => isFixable(d) && isSizeField(d.field)).map(d => selKey(f, d.field))));

/** Ένα κομμάτι (πλήθος, ημερομηνία) μέσα σε μεταφρασμένη πρόταση, με έντονα — όπως ήταν πριν τη μετάφραση. */
const emphasize = (text: string, part: string): React.ReactNode => {
  const at = part ? text.indexOf(part) : -1;
  if (at < 0) return text;
  return <>{text.slice(0, at)}<strong className="font-semibold text-t1">{part}</strong>{text.slice(at + part.length)}</>;
};

/** Ένας αριθμός στον πίνακα καταστημάτων: «—» για το μηδέν, χρώμα μόνο όταν κάτι θέλει δουλειά. */
const tallyCell = (n: number, tone: 'ok' | 'warn' | 'brand' | 'muted') => {
  if (!n) return <span className="text-t4">—</span>;
  const color = tone === 'ok' ? 'text-emerald-700 dark:text-emerald-400'
    : tone === 'warn' ? 'text-amber-700 dark:text-amber-400'
      : tone === 'brand' ? 'text-brand' : 'text-t2';
  return <span className={`font-semibold tabular-nums ${color}`}>{n}</span>;
};

interface Props {
  orgId: number;
  canManage: boolean;
}

const OrgCatalogConsistency: React.FC<Props> = ({ orgId, canManage }) => {
  const { t } = useTranslation(['orgPages', 'common']);
  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  // message: το μήνυμα του διακομιστή (αλλιώς το γενικό «Η σύγκριση απέτυχε» στη ζωγραφιά)· status για το 403.
  const [error, setError] = useState<{ message: string | null; status?: number } | null>(null);
  const [toast, setToast] = useState<FlashMessage | null>(null);

  const [storeFilter, setStoreFilter] = useState<number | null>(null);
  const [kindFilter, setKindFilter] = useState<Kind | null>(null);
  const [includeLow, setIncludeLow] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirm, setConfirm] = useState(false);
  const [confirmApprove, setConfirmApprove] = useState(false);
  const [approving, setApproving] = useState(false);
  const [openGroups, setOpenGroups] = useState<Set<Kind>>(new Set<Kind>(['missing', 'extra', 'drift']));

  const load = useCallback(async (fresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const data = await organizationAPI.getCatalogConsistency(orgId, { fresh, includeLow });
      setReport(data);
      setSelected(new Set());   // τα παλιά ευρήματα μπορεί να μην ισχύουν πια
    } catch (e: any) {
      setError({ message: e?.response?.data?.error || null, status: e?.response?.status });
    } finally {
      setLoading(false);
    }
  }, [orgId, includeLow]);

  useEffect(() => { load(false); }, [load]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  const visible = useMemo(() => {
    if (!report) return [];
    return report.findings.filter(f =>
      (storeFilter === null || f.store_id === storeFilter) &&
      (kindFilter === null || f.kind === kindFilter));
  }, [report, storeFilter, kindFilter]);

  const grouped = useMemo(() => {
    const g: Record<Kind, Finding[]> = { missing: [], extra: [], drift: [], local: [] };
    for (const f of visible) g[f.kind].push(f);
    return g;
  }, [visible]);

  // Όλα τα κλειδιά που μπορούν να επιλεγούν σε μια ομάδα. Στις αποκλίσεις η
  // επιλογή είναι ανά στοιχείο· σε κάθε άλλο είδος, μία ανά προϊόν.
  const selectableKeys = useCallback((items: Finding[]): string[] => {
    // Set: ένα μέγεθος μπορεί να έχει δύο γραμμές με το ίδιο κλειδί `size:<id>`.
    const out = new Set<string>();
    for (const f of items) {
      if (f.kind === 'drift') {
        for (const d of f.fields) if (isFixable(d)) out.add(selKey(f, d.field));
      } else out.add(selKey(f));
    }
    return Array.from(out);
  }, []);

  const toggle = (key: string) => setSelected(prev => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  const toggleMany = (keys: string[], on: boolean) => setSelected(prev => {
    const next = new Set(prev);
    for (const k of keys) on ? next.add(k) : next.delete(k);
    return next;
  });

  // Οι επιλογές γίνονται ενέργειες: μία ανά (είδος, κατάστημα, προϊόν), με τα
  // επιλεγμένα στοιχεία μαζεμένα σε μία γραμμή για τις αποκλίσεις.
  const actions = useMemo(() => {
    if (!report) return [];
    const byRow = new Map<string, { kind: Kind; store_id: number; org_product_id?: number; product_id?: number; fields: string[] }>();
    for (const f of report.findings) {
      if (f.kind === 'local') {
        if (f.product_id != null && selected.has(selKey(f)))
          byRow.set(`local:${f.store_id}:${f.product_id}`,
            { kind: 'local', store_id: f.store_id, product_id: f.product_id, fields: [] });
        continue;
      }
      if (f.org_product_id == null) continue;
      if (f.kind === 'drift') {
        for (const d of f.fields) {
          // Επιτρεπτή παρέκκλιση / τοπικό μέγεθος: δεν στέλνεται ποτέ (η διόρθωση δεν τα αλλάζει).
          if (!isFixable(d) || !selected.has(selKey(f, d.field))) continue;
          const rk = `${f.store_id}:${f.org_product_id}`;
          if (!byRow.has(rk)) byRow.set(rk, { kind: 'drift', store_id: f.store_id, org_product_id: f.org_product_id, fields: [] });
          byRow.get(rk)!.fields.push(d.field);
        }
      } else if (selected.has(selKey(f))) {
        byRow.set(`${f.kind}:${f.store_id}:${f.org_product_id}`,
          { kind: f.kind, store_id: f.store_id, org_product_id: f.org_product_id, fields: [] });
      }
    }
    return Array.from(byRow.values());
  }, [report, selected]);

  const counts = useMemo(() => {
    const c = { missing: 0, extra: 0, drift: 0, local: 0 };
    for (const a of actions) if (a.kind in c) (c as any)[a.kind]++;
    return c;
  }, [actions]);

  const apply = async () => {
    setApplying(true);
    setConfirm(false);
    try {
      const payload = actions.map(a => a.kind === 'local'
        ? { kind: 'local', store_id: a.store_id, product_id: a.product_id as number }
        : {
          kind: a.kind, store_id: a.store_id, org_product_id: a.org_product_id as number,
          ...(a.kind === 'drift' ? { fields: a.fields } : {}),
        });
      const res = await organizationAPI.applyCatalogConsistency(orgId, payload);
      setToast({
        type: res.failed?.length ? 'error' : 'success',
        message: res.message || t('orgPages:consistency.done'),
      });
      await load(true);
    } catch (e: any) {
      setToast({ type: 'error', message: e?.response?.data?.error || t('orgPages:consistency.applyFailed') });
    } finally {
      setApplying(false);
    }
  };

  // Όταν υπάρχει ενεργό φίλτρο καταστήματος, η έγκριση περιορίζεται σε αυτό —
  // αλλιώς ένα πάτημα θα άγγιζε και τα 26 χωρίς να το περιμένει ο χειριστής.
  const pending = report?.pending_sync;
  const pendingScope = useMemo(() => {
    if (!pending) return { n: 0, stores: 0, ids: null as number[] | null, label: '' };
    if (storeFilter !== null) {
      const row = pending.by_store.find(s => s.store_id === storeFilter);
      return { n: row?.n || 0, stores: row ? 1 : 0, ids: [storeFilter], label: row ? row.store_name : '' };
    }
    return { n: pending.total, stores: pending.stores, ids: null, label: '' };
  }, [pending, storeFilter]);

  const approvePending = async () => {
    setApproving(true);
    setConfirmApprove(false);
    try {
      const res = await organizationAPI.approvePendingSync(orgId, pendingScope.ids || undefined);
      setToast({ type: res.failed?.length ? 'error' : 'success', message: res.message || t('orgPages:consistency.done') });
      await load(true);
    } catch (e: any) {
      setToast({
        type: 'error',
        message: e?.response?.status === 403
          ? t('orgPages:consistency.pending.forbidden')
          : (e?.response?.data?.error || t('orgPages:consistency.pending.failed')),
      });
    } finally {
      setApproving(false);
    }
  };

  const greekDate = (iso: string | null) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('el-GR', { day: 'numeric', month: 'long' });
  };

  const waitingOf = useCallback((storeId: number) => pending?.by_store.find(p => p.store_id === storeId)?.n || 0, [pending]);

  const storeColumns = useMemo<Column<StoreTally>[]>(() => {
    const kindColumn = (k: Kind, width: string, hideBelow?: 'medium'): Column<StoreTally> => ({
      key: k,
      header: t(`orgPages:consistency.kinds.${k}.short`),
      width,
      align: 'right',
      hideBelow,
      render: s => tallyCell(s[k], KIND_TONE[k]),
      stackRender: s => (s[k] ? t('orgPages:consistency.stores.stackCount', { label: t(`orgPages:consistency.kinds.${k}.short`), count: s[k] }) : null),
    });
    return [
      {
        key: 'store_name',
        header: t('orgPages:consistency.stores.store'),
        width: 'minmax(180px, 2fr)',
        render: s => {
          const clean = !s.missing && !s.extra && !s.drift && !waitingOf(s.store_id);
          return (
            <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
              <span className="min-w-0 break-words font-medium text-t1">{s.store_name}</span>
              {clean && <StatusChip tone="ok">{t('orgPages:consistency.stores.agrees')}</StatusChip>}
            </span>
          );
        },
      },
      {
        key: 'expected',
        header: t('orgPages:consistency.stores.expected'),
        width: '100px',
        align: 'right',
        hideBelow: 'medium',
        render: s => <span className="tabular-nums text-t3">{s.expected}</span>,
      },
      kindColumn('missing', '92px'),
      kindColumn('extra', '112px'),
      kindColumn('drift', '100px'),
      kindColumn('local', '84px', 'medium'),
      {
        key: 'waiting',
        header: <span title={t('orgPages:consistency.stores.waitingHint')}>{t('orgPages:consistency.stores.waiting')}</span>,
        width: '100px',
        align: 'right',
        render: s => tallyCell(waitingOf(s.store_id), 'warn'),
        stackRender: s => {
          const n = waitingOf(s.store_id);
          return n ? t('orgPages:consistency.stores.stackCount', { label: t('orgPages:consistency.stores.waiting'), count: n }) : null;
        },
      },
    ];
  }, [t, waitingOf]);

  const title = t('orgPages:consistency.title');

  if (loading && !report) {
    return (
      <ListPage embedded title={title} testId="org-consistency">
        <div role="status" className="space-y-3 py-4" data-testid="consistency-loading">
          <p className="text-sm text-t3">{t('orgPages:consistency.loading')}</p>
          {['52%', '74%', '40%', '66%', '58%'].map((w, i) => (
            <span key={i} aria-hidden="true" className="lp-skel block" style={{ width: w }} />
          ))}
        </div>
      </ListPage>
    );
  }

  if (error && !report) {
    return (
      <ListPage embedded title={title} testId="org-consistency">
        <LoadFailed
          message={error.status === 403
            ? t('orgPages:common.forbidden')
            : (error.message || t('orgPages:consistency.loadFailed'))}
          onRetry={() => load(true)}
        />
      </ListPage>
    );
  }

  if (!report) return null;

  const allClean = report.totals.missing === 0 && report.totals.extra === 0 && report.totals.drift === 0;
  const filteredStore = storeFilter !== null ? report.stores.find(s => s.store_id === storeFilter) : null;

  return (
    <ListPage embedded title={title} testId="org-consistency">
      <FlashToast toast={toast} />

      {/* Μία πρόταση: πόσα έχει ο κεντρικός, τι πρέπει να ισχύει */}
      <p className="pb-3 text-sm text-t3">
        {emphasize(t('orgPages:consistency.intro', { count: report.org_products }), String(report.org_products))}
        {' '}{t('orgPages:consistency.introRule')}
      </p>

      <ListToolbar
        filters={(
          <>
            {/* Σύνοψη ανά είδος — και φίλτρο (ως 14/10: τέσσερις χρωματιστές κάρτες). Με «καθαρό»
                κατάλογο δεν υπάρχουν ευρήματα να φιλτραριστούν, όπως και πριν δεν υπήρχαν κάρτες. */}
            {!(allClean && report.totals.local === 0) && (
              <div className="lp-seg" role="radiogroup" aria-label={t('orgPages:consistency.kindLabel')}>
                <button type="button" role="radio" aria-checked={kindFilter === null} onClick={() => setKindFilter(null)}>
                  {t('orgPages:consistency.kindAll')}
                </button>
                {KIND_ORDER.map(k => (
                  <button key={k} type="button" role="radio" aria-checked={kindFilter === k}
                    title={t(`orgPages:consistency.kinds.${k}.title`)}
                    onClick={() => setKindFilter(kindFilter === k ? null : k)}>
                    {t(`orgPages:consistency.kinds.${k}.short`)}
                    <span className="ml-1.5 tabular-nums text-t3">{report.totals[k]}</span>
                  </button>
                ))}
              </div>
            )}
            <label className="inline-flex min-h-[44px] cursor-pointer select-none items-center gap-2 text-[13.5px] text-t2">
              <input type="checkbox" checked={includeLow} onChange={e => setIncludeLow(e.target.checked)}
                className="h-4 w-4 shrink-0 accent-[hsl(var(--brand))]" />
              {t('orgPages:consistency.includeLow')}
            </label>
          </>
        )}
        actions={(
          <button type="button" onClick={() => load(true)} disabled={loading} className={GHOST_BTN}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
            {t('orgPages:common.refresh')}
          </button>
        )}
        summary={storeFilter !== null ? (
          <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
            <span>{t('orgPages:consistency.stores.filter')} <strong className="font-semibold text-t1">{filteredStore?.store_name || `#${storeFilter}`}</strong></span>
            <button type="button" onClick={() => setStoreFilter(null)} className={LINK_BTN}>
              {t('orgPages:consistency.stores.clearFilter')}
            </button>
          </span>
        ) : null}
      />

      <div className="space-y-3">
        {/* Εκκρεμείς κεντρικές αλλαγές — λιμνάζουν όταν τα καταστήματα δεν τις εγκρίνουν */}
        {pending && pending.total > 0 && (
          <Banner tone="warn" testId="consistency-pending" action={canManage && pendingScope.n > 0 ? (
            <button type="button" onClick={() => setConfirmApprove(true)} disabled={approving} className={PRIMARY_BTN}>
              {approving && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              {storeFilter !== null
                ? t('orgPages:consistency.pending.approveStore', { count: pendingScope.n })
                : t('orgPages:consistency.pending.approveAll', { count: pendingScope.n })}
            </button>
          ) : undefined}>
            <p className="font-semibold">{t('orgPages:consistency.pending.title', { count: pending.total })}</p>
            <p className="mt-0.5 text-[13px] opacity-90">
              {t('orgPages:consistency.pending.waiting', { count: pending.stores })}
              {pending.oldest && (
                <>
                  {' · '}
                  {emphasize(t('orgPages:consistency.pending.oldest', { date: greekDate(pending.oldest) }), greekDate(pending.oldest))}
                </>
              )}
              {'. '}{t('orgPages:consistency.pending.hint')}
            </p>
            {pending.by_store.length > 1 && (
              <p className="mt-1 text-xs opacity-80">
                {pending.by_store.slice(0, 5).map(s => `${s.store_name} (${s.n})`).join(' · ')}
                {pending.by_store.length > 5 ? ` · ${t('orgPages:consistency.pending.more', { count: pending.by_store.length - 5 })}` : ''}
              </p>
            )}
          </Banner>
        )}

        {/* Τιμοκατάλογοι με τιμή που δεν φτάνει σε κάποιο κανάλι. Δεν είναι λάθος
            καταστήματος (κάνει ό,τι λέει ο κανόνας), γι' αυτό δεν διορθώνεται από εδώ:
            η διόρθωση είναι στη λίστα, από την οθόνη τιμοκαταλόγων. */}
        {(report.price_list_gaps || []).map(g => (
          <details key={g.price_list_id} data-testid={`consistency-gap-${g.price_list_id}`}
            className="group rounded-xl border border-amber-500/35 bg-amber-500/10 px-3.5 py-2.5 text-amber-900 dark:text-amber-200">
            <summary className="flex cursor-pointer list-none items-start gap-3 rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-line">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold">
                  {t('orgPages:consistency.gaps.title', { name: g.name, count: g.items.length })}
                </p>
                <p className="mt-0.5 text-[13px] opacity-90">
                  {t('orgPages:consistency.gaps.explain')}
                  <span className="opacity-80"> · {g.stores.join(', ')}</span>
                </p>
              </div>
              <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 transition-transform group-open:rotate-90" aria-hidden="true" />
            </summary>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-sm text-t1">
                <thead style={{ background: 'transparent' }}>
                  <tr className="text-left text-[12.5px] text-t3">
                    <th className="py-1 pr-3 font-semibold">{t('orgPages:consistency.gaps.product')}</th>
                    <th className="py-1 pr-3 font-semibold">{t('orgPages:consistency.gaps.channel')}</th>
                    <th className="py-1 pr-3 text-right font-semibold">{t('orgPages:consistency.gaps.list')}</th>
                    <th className="py-1 text-right font-semibold">{t('orgPages:consistency.gaps.charged')}</th>
                  </tr>
                </thead>
                <tbody>
                  {g.items.map((i, n) => (
                    <tr key={n} className="border-t border-line">
                      <td className="py-1.5 pr-3">{i.product_name}{i.size_name ? <span className="text-t3"> · {i.size_name}</span> : null}</td>
                      <td className="py-1.5 pr-3 text-t3">{i.channel}</td>
                      <td className="py-1.5 pr-3 text-right tabular-nums text-t2">{i.list_price}</td>
                      <td className="py-1.5 text-right font-semibold tabular-nums text-amber-800 dark:text-amber-300">{i.charged}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        ))}
      </div>

      {allClean && report.totals.local === 0 ? (
        <EmptyState icon={CheckCircle2} title={t('orgPages:consistency.clean')} testId="consistency-clean" />
      ) : (
        <>
          {/* Καταστήματα — κλικ σε γραμμή = φίλτρο σε αυτό το κατάστημα (ξανά κλικ = όλα) */}
          <section className="mt-5" aria-label={t('orgPages:consistency.stores.title')}>
            <div className="max-h-[22rem] overflow-y-auto">
              <DataTable
                columns={storeColumns}
                rows={report.stores}
                rowKey={s => s.store_id}
                onRowClick={s => setStoreFilter(storeFilter === s.store_id ? null : s.store_id)}
                selectedKey={storeFilter}
                dense
                stickyHeader
                ariaLabel={t('orgPages:consistency.stores.title')}
                rowLabel={s => t('orgPages:consistency.stores.rowLabel', { name: s.store_name })}
                empty={t('orgPages:consistency.stores.empty')}
                testId="consistency-stores"
              />
            </div>
          </section>

          {/* Ευρήματα ανά είδος */}
          {KIND_ORDER.map(k => {
            const items = grouped[k];
            if (!items.length) return null;
            const open = openGroups.has(k);
            const keys = selectableKeys(items);
            const allOn = keys.length > 0 && keys.every(x => selected.has(x));
            const kindTitle = t(`orgPages:consistency.kinds.${k}.title`);

            return (
              <section key={k} className="mt-6 border-t border-line pt-4" data-testid={`consistency-group-${k}`}>
                <div className="flex items-start gap-2">
                  <button type="button" aria-expanded={open} aria-label={kindTitle}
                    onClick={() => setOpenGroups(p => {
                      const n = new Set(p); n.has(k) ? n.delete(k) : n.add(k); return n;
                    })}
                    className="-ml-1 mt-px inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-t3 hover:bg-surface-3 hover:text-t1">
                    {open ? <ChevronDown className="h-4 w-4" aria-hidden="true" /> : <ChevronRight className="h-4 w-4" aria-hidden="true" />}
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-[15px] font-semibold text-t1">{kindTitle}</h3>
                      <StatusChip tone={KIND_TONE[k]}>{items.length}</StatusChip>
                    </div>
                    <p className="mt-0.5 text-[13px] text-t3">{t(`orgPages:consistency.kinds.${k}.explain`)}</p>
                    <p className="mt-1 flex items-start gap-1.5 text-[13px] text-t2">
                      <Wrench className="mt-0.5 h-3.5 w-3.5 shrink-0 text-t4" aria-hidden="true" />
                      <span>{t(`orgPages:consistency.kinds.${k}.action`)}</span>
                    </p>
                  </div>
                  {canManage && keys.length > 0 && (
                    <button type="button" onClick={() => toggleMany(keys, !allOn)} className={`${LINK_BTN} shrink-0`}>
                      {allOn ? t('orgPages:consistency.group.none') : t('orgPages:consistency.group.all')}
                    </button>
                  )}
                </div>

                {open && (
                  <div className="mt-2 max-h-[26rem] divide-y divide-line overflow-y-auto border-y border-line">
                    {items.map(f => (
                      <div key={selKey(f)} className="flex items-start gap-3 px-1 py-2.5">
                        {canManage && f.kind !== 'drift' && (
                          <input type="checkbox" className="mt-0.5 h-4 w-4 shrink-0 accent-[hsl(var(--brand))]"
                            aria-label={t('orgPages:consistency.select', { name: f.name, store: f.store_name })}
                            checked={selected.has(selKey(f))} onChange={() => toggle(selKey(f))} />
                        )}
                        {f.kind === 'drift' && <div className="w-4 shrink-0" />}
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                            <span className="text-sm font-medium text-t1">{f.name}</span>
                            <span className="text-xs text-t3">{f.store_name}</span>
                            {f.category_name && <span className="text-xs text-t4">· {f.category_name}</span>}
                          </div>
                          {f.kind === 'drift' && (
                            <div className="mt-1.5 space-y-1.5">
                              {/* key: ένα μέγεθος μπορεί να έχει ΔΥΟ γραμμές με το ίδιο field `size:<id>`
                                  (διορθώσιμη + επιτρεπτή παρέκκλιση) — μοναδικό ανά γραμμή με τη θέση. */}
                              {f.fields.map((d, i) => isFixable(d) ? (
                                <label key={`${d.field}:${i}`} className="flex cursor-pointer flex-wrap items-center gap-x-2 gap-y-0.5 text-[13px]"
                                  title={isSizeField(d.field) ? t('orgPages:consistency.drift.sizesTogether') : undefined}>
                                  {canManage && (
                                    <input type="checkbox" className="h-4 w-4 shrink-0 accent-[hsl(var(--brand))]"
                                      checked={selected.has(selKey(f, d.field))}
                                      onChange={() => (isSizeField(d.field)
                                        // Το push μεγεθών είναι ένα ανά προϊόν: τα μεγέθη επιλέγονται μαζί.
                                        ? toggleMany(sizeKeysOf(f), !selected.has(selKey(f, d.field)))
                                        : toggle(selKey(f, d.field)))} />
                                  )}
                                  <span className={`shrink-0 ${d.severity === 'high' ? 'font-medium text-t1' : 'text-t3'}`}>{d.label}:</span>
                                  <span className="text-red-700 line-through decoration-1 dark:text-red-400">{d.store}</span>
                                  <span className="text-t4" aria-hidden="true">→</span>
                                  <span className="font-medium text-emerald-700 dark:text-emerald-400">{d.central}</span>
                                  {d.severity === 'high' && <StatusChip tone="danger">{t('orgPages:consistency.drift.important')}</StatusChip>}
                                </label>
                              ) : (
                                // F1-W5-8: επιτρεπτή παρέκκλιση μεγέθους ή τοπικό μέγεθος — φαίνεται, δεν επιλέγεται:
                                // η διόρθωση δεν το αλλάζει (όπως τις παρεκκλίσεις προϊόντος).
                                <div key={`${d.field}:${i}`} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[13px]">
                                  {canManage && <span className="w-4 shrink-0" />}
                                  <span className="shrink-0 text-t3">{d.label}:</span>
                                  <span className="text-t2">{d.store}</span>
                                  {d.allowed && (
                                    <>
                                      <span className="text-t4">{t('orgPages:consistency.drift.centralWas', { value: d.central })}</span>
                                      <StatusChip tone="muted" title={t('orgPages:consistency.drift.allowedHint')}>{t('orgPages:consistency.drift.allowed')}</StatusChip>
                                    </>
                                  )}
                                  {!d.allowed && d.local && (
                                    <StatusChip tone="muted" title={t('orgPages:consistency.drift.localHint')}>{t('orgPages:consistency.drift.local')}</StatusChip>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                          {f.kind === 'local' && (
                            <p className="mt-0.5 text-xs text-t3">{t('orgPages:consistency.localHint')}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            );
          })}

          {visible.length === 0 && (
            <div className="mt-4"><EmptyState title={t('orgPages:consistency.noMatches')} testId="consistency-no-matches" /></div>
          )}
        </>
      )}

      {/* Μπάρα ενεργειών — εμφανίζεται μόνο όταν υπάρχει επιλογή */}
      {canManage && actions.length > 0 && (
        <div data-testid="consistency-actionbar"
          className="sticky bottom-4 z-30 mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-brand-line bg-surface-1 px-4 py-3 shadow-[0_12px_32px_-12px_rgb(0_0_0_/_0.3)]">
          <div className="min-w-0 flex-1 basis-64 text-sm text-t2">
            {emphasize(t('orgPages:consistency.bar.selected', { count: actions.length }), String(actions.length))}
            <span className="text-t3">
              {counts.missing ? ` · ${t('orgPages:consistency.bar.missing', { count: counts.missing })}` : ''}
              {counts.extra ? ` · ${t('orgPages:consistency.bar.extra', { count: counts.extra })}` : ''}
              {counts.drift ? ` · ${t('orgPages:consistency.bar.drift', { count: counts.drift })}` : ''}
              {counts.local ? ` · ${t('orgPages:consistency.bar.local', { count: counts.local })}` : ''}
            </span>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button type="button" onClick={() => setSelected(new Set())} className={GHOST_BTN}>
              {t('orgPages:consistency.bar.cancel')}
            </button>
            <button type="button" onClick={() => setConfirm(true)} disabled={applying} className={PRIMARY_BTN}>
              {applying ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Wrench className="h-4 w-4" aria-hidden="true" />}
              {t('orgPages:consistency.bar.fix')}
            </button>
          </div>
        </div>
      )}

      {/* Επιβεβαίωση μαζικής έγκρισης */}
      <DetailPanel
        open={confirmApprove && !!pending}
        onClose={() => setConfirmApprove(false)}
        title={`${t('orgPages:consistency.approve.title', { count: pendingScope.n })}${storeFilter !== null && pendingScope.label ? ` — ${pendingScope.label}` : ''}`}
        footer={(
          <>
            <button type="button" onClick={() => setConfirmApprove(false)} className={GHOST_BTN}>{t('orgPages:common.cancel')}</button>
            <button type="button" onClick={approvePending} className={PRIMARY_BTN}>{t('orgPages:consistency.approve.confirm')}</button>
          </>
        )}
        testId="consistency-approve"
      >
        <div className="space-y-4 text-sm text-t2">
          <p>
            {storeFilter !== null
              ? t('orgPages:consistency.approve.bodyStore')
              : t('orgPages:consistency.approve.bodyStores', { count: pendingScope.stores })}
          </p>
          <ul className="list-inside list-disc space-y-1.5 text-[13px] text-t3">
            <li>{t('orgPages:consistency.approve.rulePrices')}</li>
            <li>{t('orgPages:consistency.approve.ruleOverrides')}</li>
            <li>{t('orgPages:consistency.approve.ruleDeleted')}</li>
            <li>{t('orgPages:consistency.approve.ruleNewest')}</li>
          </ul>
          <p className="text-xs text-t4">{t('orgPages:consistency.f5')}</p>
        </div>
      </DetailPanel>

      {/* Επιβεβαίωση — λέει ΤΙ θα γίνει, όχι «είστε σίγουροι;» */}
      <DetailPanel
        open={confirm}
        onClose={() => setConfirm(false)}
        title={t('orgPages:consistency.apply.title')}
        footer={(
          <>
            <button type="button" onClick={() => setConfirm(false)} className={GHOST_BTN}>{t('orgPages:common.cancel')}</button>
            <button type="button" onClick={apply} className={PRIMARY_BTN}>{t('orgPages:consistency.apply.confirm')}</button>
          </>
        )}
        testId="consistency-confirm"
      >
        <div className="space-y-4">
          <ul className="space-y-2.5 text-sm text-t2">
            {KIND_ORDER.filter(k => counts[k] > 0).map(k => (
              <li key={k} className="flex gap-2.5">
                <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-[hsl(var(--text-4))]" aria-hidden="true" />
                <span>{emphasize(t(`orgPages:consistency.apply.${k}`, { count: counts[k] }), String(counts[k]))}</span>
              </li>
            ))}
          </ul>
          <p className="text-xs text-t4">
            {t('orgPages:consistency.approve.rulePrices')} {t('orgPages:consistency.f5')}
          </p>
        </div>
      </DetailPanel>
    </ListPage>
  );
};

export default OrgCatalogConsistency;
